// Enhanced CompatibleProtectedRoute with cross-tab session detection
import { Loader2 } from 'lucide-react'
import React, { useContext, useEffect, useState, useCallback, useRef } from 'react'
import { Navigate, Outlet, useNavigate } from 'react-router'
import { authSessionStorageService } from '@/services/sessionStorageService'
import { AuthContext } from './AuthContext'
import { 
  sessionIntegrityValidation, 
  securityEventLogger,
  authErrorHandler 
} from '@/services/securityValidationUtils'

// Compatibility mode flag - should match your AuthService setting
const COMPATIBILITY_MODE = true

interface SecurityCheckState {
  sessionValid: boolean
  integrityValid: boolean
  timeoutValid: boolean
  lastCheckTime: number
  securityScore: number
}

interface CrossTabSessionState {
  hasCrossTabSession: boolean
  isValidCrossTabSession: boolean
  syncInProgress: boolean
  syncCompleted: boolean
}

export const CompatibleProtectedRoute: React.FC = () => {
  const navigate = useNavigate()
  const { isAuthenticated, isLoading } = useContext(AuthContext)
  const [securityChecks, setSecurityChecks] = useState<SecurityCheckState>({
    sessionValid: false,
    integrityValid: true, // Default to true in compatibility mode
    timeoutValid: true,   // Default to true in compatibility mode
    lastCheckTime: 0,
    securityScore: 100    // Default to max score in compatibility mode
  })
  
  const [crossTabState, setCrossTabState] = useState<CrossTabSessionState>({
    hasCrossTabSession: false,
    isValidCrossTabSession: false,
    syncInProgress: false,
    syncCompleted: false
  })
  
  const [isPerformingSecurityCheck, setIsPerformingSecurityCheck] = useState(true)
  const [crossTabCheckComplete, setCrossTabCheckComplete] = useState(false)
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const securityCheckTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  
  // Security check intervals
  const SECURITY_CHECK_INTERVAL = COMPATIBILITY_MODE ? 10 * 60 * 1000 : 5 * 60 * 1000 // 10 min in compat mode
  const INACTIVITY_TIMEOUT = 30 * 60 * 1000 // 30 minutes
  const MIN_SECURITY_SCORE = COMPATIBILITY_MODE ? 30 : 70 // Lower threshold in compat mode

  /**
   * NEW: Check for cross-tab session before making routing decisions
   */
  const checkCrossTabSession = useCallback(async (): Promise<CrossTabSessionState> => {
    const sessionData = authSessionStorageService.getAllSessionData()
    const hasLocalSession = !!(sessionData.sessionId && sessionData.loginTime)
    
    // Check for cross-tab session indicators
    const lastActivity = localStorage.getItem('session_last_activity')
    const sessionTimer = localStorage.getItem('session_timer_start')
    const masterTab = localStorage.getItem('session_master_tab')
    
    const hasCrossTabSession = !!(lastActivity && sessionTimer)
    let isValidCrossTabSession = false
    
    if (hasCrossTabSession) {
      // Check if cross-tab session is still valid (within 30 minutes)
      const sessionAge = Date.now() - parseInt(sessionTimer)
      isValidCrossTabSession = sessionAge < (30 * 60 * 1000)
      
      console.log('🔗 ProtectedRoute: Cross-tab session analysis:', {
        hasLocalSession,
        hasCrossTabSession,
        isValidCrossTabSession,
        sessionAge: Math.round(sessionAge / 60000),
        masterTab: !!masterTab
      })
    }
    
    return {
      hasCrossTabSession,
      isValidCrossTabSession,
      syncInProgress: false,
      syncCompleted: false
    }
  }, [])

  /**
   * NEW: Handle cross-tab session sync
   */
  const handleCrossTabSync = useCallback(async (crossTabState: CrossTabSessionState): Promise<boolean> => {
    if (!crossTabState.isValidCrossTabSession) {
      return false
    }
    
    const sessionData = authSessionStorageService.getAllSessionData()
    const hasLocalSession = !!(sessionData.sessionId && sessionData.loginTime)
    
    if (hasLocalSession) {
      // Already have local session, no sync needed
      return true
    }
    
    console.log('🔗 ProtectedRoute: Starting cross-tab session sync...')
    
    setCrossTabState(prev => ({ ...prev, syncInProgress: true }))
    
    // Give time for cross-tab communication to happen
    await new Promise(resolve => setTimeout(resolve, 1200))
    
    // Check if session data is now available
    const updatedSessionData = authSessionStorageService.getAllSessionData()
    const nowHasLocalSession = !!(updatedSessionData.sessionId && updatedSessionData.loginTime)
    
    console.log('🔗 ProtectedRoute: Cross-tab sync result:', {
      beforeSync: hasLocalSession,
      afterSync: nowHasLocalSession,
      sessionId: !!updatedSessionData.sessionId,
      loginTime: !!updatedSessionData.loginTime
    })
    
    setCrossTabState(prev => ({ 
      ...prev, 
      syncInProgress: false, 
      syncCompleted: true 
    }))
    
    return nowHasLocalSession
  }, [])

  /**
   * Enhanced security validation with cross-tab session support
   */
  const performSecurityChecks = useCallback(async (): Promise<SecurityCheckState> => {
    const startTime = Date.now()
    
    try {
      const allSessionData = authSessionStorageService.getAllSessionData()
      
      // Basic session validation
      const sessionValid = !!(allSessionData.loginTime && allSessionData.sessionId)
      
      let integrityResult
      let integrityValid = true
      let securityScore = 100
      
      if (COMPATIBILITY_MODE) {
        // Lenient validation in compatibility mode
        if (sessionValid) {
          // Only check for major issues
          const hasBasicData = !!(allSessionData.sessionId && allSessionData.loginTime)
          integrityValid = hasBasicData
          securityScore = hasBasicData ? 95 : 50
          
          // Simple timeout check
          if (allSessionData.loginTime) {
            const sessionAge = Date.now() - allSessionData.loginTime
            const maxAge = 24 * 60 * 60 * 1000 // 24 hours
            if (sessionAge > maxAge) {
              integrityValid = false
              securityScore = 20
            }
          }
        } else {
          integrityValid = true // Don't fail on missing session in compat mode
          securityScore = 0
        }
      } else {
        // Full integrity validation
        integrityResult = sessionIntegrityValidation.validateSessionIntegrity(allSessionData)
        integrityValid = integrityResult.isValid
        securityScore = integrityResult.securityScore
      }
      
      // Timeout validation
      let timeoutValid = true
      if (allSessionData.loginTime) {
        const sessionAge = Date.now() - allSessionData.loginTime
        const maxAge = 24 * 60 * 60 * 1000 // 24 hours
        timeoutValid = sessionAge <= maxAge
      }
      
      const newState: SecurityCheckState = {
        sessionValid,
        integrityValid,
        timeoutValid,
        lastCheckTime: startTime,
        securityScore
      }
      
      // Log security check results
      securityEventLogger.logSecurityValidation('protected_route_security_check', {
        sessionValid,
        integrityValid,
        timeoutValid,
        securityScore,
        compatibilityMode: COMPATIBILITY_MODE,
        crossTabSync: crossTabState.syncCompleted,
        issues: integrityResult?.issues?.slice(0, 3) || [],
        duration: Date.now() - startTime
      })
      
      return newState
    } catch (error) {
      securityEventLogger.logSecurityEvent('security_check_error', {
        error: authErrorHandler.createSecureError(error, 'performSecurityChecks'),
        compatibilityMode: COMPATIBILITY_MODE,
        duration: Date.now() - startTime
      }, 'error')
      
      // In compatibility mode, don't fail on security check errors
      return {
        sessionValid: COMPATIBILITY_MODE ? true : false,
        integrityValid: COMPATIBILITY_MODE ? true : false,
        timeoutValid: COMPATIBILITY_MODE ? true : false,
        lastCheckTime: startTime,
        securityScore: COMPATIBILITY_MODE ? 50 : 0
      }
    }
  }, [crossTabState.syncCompleted])

  /**
   * Handles security check results with compatibility mode logic
   */
  const handleSecurityCheckResults = useCallback((checks: SecurityCheckState) => {
    setSecurityChecks(checks)
    
    // In compatibility mode, be more lenient with navigation decisions
    if (!checks.sessionValid) {
      securityEventLogger.logSecurityEvent('route_access_denied', {
        reason: 'invalid_session',
        securityScore: checks.securityScore,
        compatibilityMode: COMPATIBILITY_MODE,
        crossTabSync: crossTabState.syncCompleted
      })
      navigate('/welcome?fromLogout=true')
      return
    }
    
    if (!COMPATIBILITY_MODE) {
      // Strict mode checks
      if (!checks.integrityValid) {
        securityEventLogger.logSessionAnomaly('Route access denied due to integrity failure', {
          securityScore: checks.securityScore
        })
        authSessionStorageService.clearSession()
        navigate('/auth/login?reason=security')
        return
      }
      
      if (!checks.timeoutValid) {
        securityEventLogger.logSessionAnomaly('Route access denied due to session timeout')
        authSessionStorageService.clearSession()
        navigate('/auth/login?reason=timeout')
        return
      }
      
      if (checks.securityScore < MIN_SECURITY_SCORE) {
        securityEventLogger.logSessionAnomaly('Route access denied due to low security score', {
          securityScore: checks.securityScore,
          minRequired: MIN_SECURITY_SCORE
        })
        authSessionStorageService.clearSession()
        navigate('/auth/login?reason=security')
        return
      }
    } else {
      // Compatibility mode - only check for severe issues
      if (checks.securityScore < MIN_SECURITY_SCORE) {
        securityEventLogger.logSecurityEvent('route_access_warning', {
          reason: 'low_security_score',
          securityScore: checks.securityScore,
          compatibilityMode: true
        }, 'warn')
        // Don't navigate away, just log warning
      }
    }
    
    // All checks passed
    setIsPerformingSecurityCheck(false)
  }, [navigate, crossTabState.syncCompleted])

  /**
   * NEW: OAuth session check - runs before other security checks
   */
  useEffect(() => {
    const checkOAuthSession = async () => {
      // Check if OAuth session was detected by OAuthWrapper
      const oauthDetected = localStorage.getItem('oauth_session_detected');
      const oauthUserData = localStorage.getItem('oauth_user_data');
      
      if (oauthDetected === 'true' && oauthUserData && !isAuthenticated) {
        console.log('🔍 ProtectedRoute: OAuth session detected, refreshing auth...');
        
        try {
          // Parse user data
          const userData = JSON.parse(oauthUserData);
          console.log('✅ ProtectedRoute: OAuth user data:', userData.userName);
          
          // Get refreshAuth from AuthContext
          const { refreshAuth } = useContext(AuthContext);
          
          // Trigger auth refresh to update AuthContext
          if (typeof refreshAuth === 'function') {
            await refreshAuth();
            console.log('✅ ProtectedRoute: OAuth authentication refreshed');
          }
          
          // Clean up OAuth flags
          localStorage.removeItem('oauth_session_detected');
          localStorage.removeItem('oauth_user_data');
          
        } catch (error) {
          console.error('❌ ProtectedRoute: Error processing OAuth session:', error);
          // Clean up on error
          localStorage.removeItem('oauth_session_detected');
          localStorage.removeItem('oauth_user_data');
        }
      }
    };

    // Run OAuth check first
    checkOAuthSession();
  }, [isAuthenticated])

  /**
   * ENHANCED: Initial security validation with cross-tab session support
   */
  useEffect(() => {
    const runInitialChecks = async () => {
      setIsPerformingSecurityCheck(true)
      
      try {
        // Step 1: Check for cross-tab sessions
        console.log('🔗 ProtectedRoute: Checking for cross-tab sessions...')
        const crossTabResult = await checkCrossTabSession()
        setCrossTabState(crossTabResult)
        
        // Step 2: Handle cross-tab sync if needed
        // Only sync if we don't already have a local session
        const sessionData = authSessionStorageService.getAllSessionData()
        const hasLocalSession = !!(sessionData.sessionId && sessionData.loginTime)

        if (crossTabResult.isValidCrossTabSession && !hasLocalSession) {
          console.log('🔗 ProtectedRoute: Valid cross-tab session found, attempting sync...')
          const syncSuccess = await handleCrossTabSync(crossTabResult)
          console.log('🔗 ProtectedRoute: Cross-tab sync result:', syncSuccess)
        }
        
        // Mark cross-tab check as complete
        setCrossTabCheckComplete(true)
        
        // Step 3: Perform security checks
        console.log('🔗 ProtectedRoute: Performing security validation...')
        const checks = await performSecurityChecks()
        handleSecurityCheckResults(checks)
        
      } catch (error) {
        console.error('🔗 ProtectedRoute: Initial checks failed:', error)
        
        securityEventLogger.logSecurityEvent('initial_security_check_failed', {
          error: authErrorHandler.createSecureError(error, 'initialSecurityCheck'),
          compatibilityMode: COMPATIBILITY_MODE
        }, 'error')
        
        if (COMPATIBILITY_MODE) {
          // In compatibility mode, don't fail on security check errors
          setCrossTabCheckComplete(true)
          setIsPerformingSecurityCheck(false)
        } else {
          // Fail secure - redirect to login
          navigate('/welcome?fromLogout=true')
        }
      }
    }
    
    runInitialChecks()
  }, []) // Only run once on mount

  /**
   * Periodic security checks (less frequent in compatibility mode)
   */
  useEffect(() => {
    if (isPerformingSecurityCheck || !crossTabCheckComplete) return
    
    const runPeriodicCheck = async () => {
      const now = Date.now()
      
      // Only run if enough time has passed since last check
      if (now - securityChecks.lastCheckTime < SECURITY_CHECK_INTERVAL) {
        return
      }
      
      try {
        const checks = await performSecurityChecks()
        
        // Only navigate if there are actual security issues
        if (!checks.sessionValid || (!COMPATIBILITY_MODE && (!checks.integrityValid || !checks.timeoutValid || 
            checks.securityScore < MIN_SECURITY_SCORE))) {
          handleSecurityCheckResults(checks)
        } else {
          // Update state without navigation
          setSecurityChecks(checks)
        }
      } catch (error) {
        securityEventLogger.logSecurityEvent('periodic_security_check_failed', {
          error: authErrorHandler.createSecureError(error, 'periodicSecurityCheck'),
          compatibilityMode: COMPATIBILITY_MODE
        }, 'error')
      }
    }
    
    // Set up periodic security checks
    securityCheckTimerRef.current = setInterval(runPeriodicCheck, SECURITY_CHECK_INTERVAL)
    
    return () => {
      if (securityCheckTimerRef.current) {
        clearInterval(securityCheckTimerRef.current)
      }
    }
  }, [isPerformingSecurityCheck, crossTabCheckComplete, securityChecks.lastCheckTime, performSecurityChecks, handleSecurityCheckResults])

  /**
   * Inactivity timer (same as before but only start after cross-tab check)
   */
  useEffect(() => {
    if (isPerformingSecurityCheck || !crossTabCheckComplete) return
    
    const resetInactivityTimer = () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current)
      }
      
      inactivityTimerRef.current = setTimeout(() => {
        securityEventLogger.logSecurityEvent('inactivity_timeout', {
          timeoutDuration: INACTIVITY_TIMEOUT,
          lastActivity: Date.now() - INACTIVITY_TIMEOUT,
          compatibilityMode: COMPATIBILITY_MODE
        })
        
        authSessionStorageService.clearSession()
        navigate('/welcome?fromLogout=true')
      }, INACTIVITY_TIMEOUT)
    }

    const activityEvents = [
      'mousemove',
      'mousedown',
      'keydown',
      'scroll',
      'touchstart',
      'click',
      'focus'
    ]

    let lastActivityTime = 0
    const activityThrottle = 1000

    const throttledResetTimer = () => {
      const now = Date.now()
      if (now - lastActivityTime >= activityThrottle) {
        lastActivityTime = now
        resetInactivityTimer()
      }
    }

    activityEvents.forEach((event) => {
      window.addEventListener(event, throttledResetTimer, { passive: true })
    })

    resetInactivityTimer()

    return () => {
      activityEvents.forEach((event) => {
        window.removeEventListener(event, throttledResetTimer)
      })
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current)
      }
    }
  }, [navigate, isPerformingSecurityCheck, crossTabCheckComplete])

  /**
   * Session maintenance on visibility change (lenient in compat mode)
   */
  useEffect(() => {
    if (!crossTabCheckComplete) return
    
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && !isPerformingSecurityCheck) {
        try {
          const checks = await performSecurityChecks()
          
          // Only redirect for session validity issues, not integrity in compat mode
          if (!checks.sessionValid || (!COMPATIBILITY_MODE && 
              (!checks.integrityValid || checks.securityScore < MIN_SECURITY_SCORE))) {
            handleSecurityCheckResults(checks)
          }
        } catch (error) {
          securityEventLogger.logSecurityEvent('visibility_change_security_check_failed', {
            error: authErrorHandler.createSecureError(error, 'visibilityChangeCheck'),
            compatibilityMode: COMPATIBILITY_MODE
          }, 'error')
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [performSecurityChecks, handleSecurityCheckResults, isPerformingSecurityCheck, crossTabCheckComplete])

  // Enhanced loading state with cross-tab sync indication
  if (isLoading || isPerformingSecurityCheck || !crossTabCheckComplete) {
    let loadingMessage = 'Loading...'
    
    if (!crossTabCheckComplete) {
      loadingMessage = crossTabState.syncInProgress ? 'Syncing session...' : 'Checking session...'
    } else if (isPerformingSecurityCheck) {
      loadingMessage = 'Validating security...'
    }
    
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader2 className="animate-spin" />
        <p
          className="ml-2 text-lg font-semibold text-gray-700"
          aria-label="Loading..."
        >
          {loadingMessage}
        </p>
      </div>
    )
  }

  // Move this BEFORE the debug log
  const shouldDenyAccess = COMPATIBILITY_MODE ? 
    (!securityChecks.sessionValid) :
    (!securityChecks.sessionValid || !securityChecks.integrityValid || 
    !securityChecks.timeoutValid || securityChecks.securityScore < MIN_SECURITY_SCORE)


  const hasOAuthSession = localStorage.getItem('oauth_session_detected') === 'true';
  const userIsAuthenticated = isAuthenticated || hasOAuthSession;

  // DEBUG: Final routing decision
  console.log('🔗 ProtectedRoute: Final routing decision:', {
    isAuthenticated,
    hasOAuthSession, // ADD THIS
    userIsAuthenticated, // ADD THIS
    sessionValid: securityChecks.sessionValid,
    shouldDenyAccess: shouldDenyAccess,
    currentPath: window.location.pathname
  })

  // Redirect if not authenticated (after all checks complete)
  if (!userIsAuthenticated) { // CHANGED: from !isAuthenticated to !userIsAuthenticated
    console.log('🔗 ProtectedRoute: Redirecting - not authenticated')
    return <Navigate to="/welcome?fromLogout=true" replace />
  }

  if (shouldDenyAccess) {
    console.log('🔗 ProtectedRoute: Redirecting - access denied')
    return <Navigate to="/welcome?fromLogout=true" replace />
  }

  // All checks passed - render protected content
  return (
    <>
      {/* Enhanced security status indicator */}
      {process.env.NODE_ENV === 'development' && (
        <div className="fixed top-0 right-0 z-50 p-2 m-2 bg-gray-800 text-white text-xs rounded">
          <div>Security Score: {securityChecks.securityScore}/100</div>
          {COMPATIBILITY_MODE && <div className="text-yellow-300">Compatibility Mode</div>}
          {crossTabState.syncCompleted && <div className="text-green-300">Cross-Tab Synced</div>}
        </div>
      )}
      <Outlet />
    </>
  )
}

export { CompatibleProtectedRoute as ProtectedRoute }